const LA_AND_PROPOSER_SAME_FIELD_KEYS = [
  "GOPAslifetobeAssuredandProposer",
  "islifetobeAssuredandProposer",
]

const SMART_GUARANTEED_PENSION_JOURNEY_IDS = [
  "smartGuaranteedPensionJourney",
]

const HEADING_TEMPLATES = {
  personalDetails: {
    single: "Personal Details of Life Assured",
    proposer: "Personal Details of Proposer",
    shared: "Personal Details of Life Assured / Proposer",
  },
  residenceOccupation: {
    single: "Life Assured Residence and Occupation Details",
    proposer: "Proposer Residence and Occupation Details",
    shared: "Life Assured / Proposer Residence and Occupation Details",
  },
  communicationAddress: {
    single: "Communication Address of Life Assured",
    proposer: "Communication Address of Proposer",
    shared: "Communication Address of Life Assured / Proposer",
  },
  occupationDetails: {
    single: "Occupation Details of Life Assured",
    proposer: "Occupation Details of Proposer",
    shared: "Occupation Details of Life Assured / Proposer",
  },
  insuranceHistory: {
    single: "Insurance History of Life Assured",
    proposer: "Insurance History of Proposer",
    shared: "Insurance History of Life Assured / Proposer",
  },
  healthLifestyle: {
    single: "Health & Lifestyle Details of Life Assured",
    proposer: "Health & Lifestyle Details of Proposer",
    shared: "Health & Lifestyle Details of Life Assured / Proposer",
  },
  nriQuestionnaires: {
    single: "NRI Questionnaires of Life Assured",
    proposer: "NRI Questionnaires of Proposer",
    shared: "NRI Questionnaires of Life Assured / Proposer",
  },
  occupationalQuestionnaire: {
    single: "Occupational Questionaire of Life Assured",
    proposer: "Occupational Questionaire of Proposer",
    shared: "Occupational Questionaire of Life Assured / Proposer",
  },
}

const SECTION_ROLE_RULES = [
  {
    role: "personalDetails",
    matches: normalizedText =>
      includesAll(normalizedText, ["personal", "detail"]),
  },
  {
    role: "residenceOccupation",
    matches: normalizedText =>
      includesAll(normalizedText, ["occupation", "detail"]) &&
      includesAny(normalizedText, ["residence", "resident"]),
  },
  {
    role: "communicationAddress",
    matches: normalizedText =>
      includesAll(normalizedText, ["communication", "address"]),
  },
  {
    role: "occupationDetails",
    matches: normalizedText =>
      includesAll(normalizedText, ["occupation", "detail"]) &&
      !includesAny(normalizedText, ["residence", "resident", "questionnaire"]),
  },
  {
    role: "insuranceHistory",
    matches: normalizedText =>
      includesAll(normalizedText, ["insurance", "history"]),
  },
  {
    role: "healthLifestyle",
    matches: normalizedText =>
      includesAll(normalizedText, ["health", "lifestyle"]),
  },
  {
    role: "nriQuestionnaires",
    matches: normalizedText =>
      includesAll(normalizedText, ["nri", "question"]),
  },
  {
    role: "occupationalQuestionnaire",
    matches: normalizedText =>
      includesAll(normalizedText, ["occupational", "question"]),
  },
]

const EXCLUDED_SECTION_TERMS = [
  "spouse",
  "joint life",
  "jointlife",
  "appointee",
  "nominee",
]

const includesAll = (text, values) =>
  values.every(value => text.includes(value))

const includesAny = (text, values) =>
  values.some(value => text.includes(value))

const normalizeText = value =>
  (value || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .toLowerCase()
    .trim()

const getYesNoValue = value => {
  if (value === "Y" || value === "N") {
    return value
  }
}

const getJourneyId = journeyState =>
  journeyState?.journeyIdFromStrapi || journeyState?.journeyId || ""

const getSectionDataValue = (sectionData, fieldKeys, parseValue) => {
  for (const fieldKey of fieldKeys) {
    const value = parseValue(sectionData?.[fieldKey])

    if (value) {
      return value
    }
  }
}

const getConditionStateValue = (conditionState, fieldKeys, parseValue) => {
  for (const [key, rawValue] of Object.entries(conditionState || {})) {
    const matchesField = fieldKeys.some(fieldKey => key.endsWith(`.${fieldKey}`))
    const value = parseValue(rawValue)

    if (matchesField && value) {
      return value
    }
  }
}

const getJourneyDataValue = (journeyState, fieldKeys, parseValue) => {
  for (const page of journeyState?.journeyData || []) {
    for (const section of page?.pageData || []) {
      const value = getSectionDataValue(section?.sectionData, fieldKeys, parseValue)

      if (value) {
        return value
      }
    }
  }
}

const getSectionDataLaAndProposerSame = sectionData => {
  return getSectionDataValue(
    sectionData,
    LA_AND_PROPOSER_SAME_FIELD_KEYS,
    getYesNoValue
  )
}

const getConditionStateLaAndProposerSame = conditionState => {
  return getConditionStateValue(
    conditionState,
    LA_AND_PROPOSER_SAME_FIELD_KEYS,
    getYesNoValue
  )
}

const getJourneyDataLaAndProposerSame = journeyState => {
  return getJourneyDataValue(
    journeyState,
    LA_AND_PROPOSER_SAME_FIELD_KEYS,
    getYesNoValue
  )
}

const inferLaAndProposerSameFromSections = sections => {
  if (Array.isArray(sections)) {
    const hasProposerSection = sections.some(section => {
      const sectionId = section?.sectionId || ""
      const sectionName = section?.name || ""

      return /proposer/i.test(sectionId) || /proposer/i.test(sectionName)
    })

    return hasProposerSection ? "N" : "Y"
  }
}

const isProposerSection = section => {
  const sectionId = section?.sectionId || ""
  const sectionName = section?.name || ""
  return /proposer/i.test(sectionId) || /proposer/i.test(sectionName)
}

const getHeadingRole = section => {
  const normalizedText = normalizeText(
    `${section?.sectionId || ""} ${section?.name || ""}`
  )

  if (!normalizedText) {
    return null
  }

  if (includesAny(normalizedText, EXCLUDED_SECTION_TERMS)) {
    return null
  }

  const matchedRule = SECTION_ROLE_RULES.find(rule =>
    rule.matches(normalizedText)
  )

  return matchedRule?.role || null
}

const getHeadingTextForRole = (role, isLaAndProposerSame, isProposer = false) => {
  const template = HEADING_TEMPLATES[role]

  if (!template) {
    return ""
  }

  if (isLaAndProposerSame === "Y") {
    return template.shared
  }

  if (isLaAndProposerSame === "N") {
    return isProposer ? template.proposer : template.single
  }

  return ""
}

const isSmartGuaranteedPensionJourney = journeyState =>
  SMART_GUARANTEED_PENSION_JOURNEY_IDS.includes(getJourneyId(journeyState))

const getSmartGuaranteedSectionMeta = section => {
  const rawText = `${section?.sectionId || ""} ${section?.name || ""} ${section?.ParentSectionName || ""} ${section?.sectionHeading || ""}`
  const normalizedText = normalizeText(rawText)

  return {
    rawText,
    normalizedText,
    isInitialPersonalDetailsSection:
      /SectionSmartGuaranteedPensionPersonalDetails/i.test(rawText),
    isInitialResidenceOccupationSection:
      /SectionCommonResidenceAndOccupation/i.test(rawText),
    isPrimaryAnnuitantSection: includesAll(normalizedText, [
      "primary",
      "annuitant",
    ]),
    isSecondaryAnnuitantSection: includesAll(normalizedText, [
      "secondary",
      "annuitant",
    ]),
    isProposerSection: normalizedText.includes("proposer"),
    isCommunicationDetailsSection: includesAll(normalizedText, [
      "communication",
      "detail",
    ]),
    isInsuranceDetailsSection: includesAll(normalizedText, [
      "insurance",
      "detail",
    ]),
    isNriQuestionnaireSection: includesAll(normalizedText, ["nri", "question"]),
    isOccupationalQuestionnaireSection: includesAll(normalizedText, [
      "occupational",
      "question",
    ]),
    isBankDetailsSection: includesAll(normalizedText, ["bank", "detail"]),
  }
}

const getSmartGuaranteedProposerPartyLabel = isLaAndProposerSame =>
  isLaAndProposerSame === "N" ? "Proposer" : "Primary Annuitant"

const getSmartGuaranteedPartyLabel = section => {
  const { normalizedText } = getSmartGuaranteedSectionMeta(section)

  if (normalizedText.includes("nominee")) {
    return "Nominee"
  }

  if (normalizedText.includes("appointee")) {
    return "Appointee"
  }
}

const appendSmartGuaranteedPartyToHeading = (heading, section) => {
  const partyLabel = getSmartGuaranteedPartyLabel(section)

  if (!heading || !partyLabel) {
    return heading || ""
  }

  if (new RegExp(`of\\s+${partyLabel}`, "i").test(heading)) {
    return heading
  }

  return `${heading} of ${partyLabel}`
}

const getSmartGuaranteedPersonalDetailsHeading = ({
  section,
  isLaAndProposerSame,
}) => {
  const {
    isInitialPersonalDetailsSection,
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (isSecondaryAnnuitantSection) {
    return "Personal Details of Secondary Annuitant"
  }

  if (isProposerSection) {
    if (isLaAndProposerSame === "N") {
      return "Personal Details of Proposer"
    }

    return "Personal Details of Primary Annuitant"
  }

  if (isPrimaryAnnuitantSection) {
    return "Personal Details of Primary Annuitant"
  }

  if (isInitialPersonalDetailsSection) {
    if (isLaAndProposerSame === "Y") {
      return "Personal Details of Primary Annuitant"
    }

    if (isLaAndProposerSame === "N") {
      return "Personal Details of Primary Annuitant"
    }

    return "Personal Details of Annuitant"
  }
}

const getSmartGuaranteedResidenceOccupationHeading = ({
  section,
  isLaAndProposerSame,
}) => {
  const {
    isInitialResidenceOccupationSection,
    isProposerSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (isProposerSection) {
    if (isLaAndProposerSame === "N") {
      return "Proposer Residence and Occupation Details"
    }

    return "Primary Annuitant Residence and Occupation Details"
  }

  if (isInitialResidenceOccupationSection) {
    if (isLaAndProposerSame === "Y") {
      return "Primary Annuitant Residence and Occupation Details"
    }

    if (isLaAndProposerSame === "N") {
      return "Primary Annuitant Residence and Occupation Details"
    }

    return "Annuitant Residence and Occupation Details"
  }
}

const getSmartGuaranteedCommunicationAddressHeading = (
  section,
  isLaAndProposerSame
) => {
  const {
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
    isCommunicationDetailsSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (!isCommunicationDetailsSection) {
    return
  }

  if (isSecondaryAnnuitantSection) {
    return "Communication Address of Secondary Annuitant"
  }

  if (isProposerSection) {
    const party = getSmartGuaranteedProposerPartyLabel(isLaAndProposerSame)
    return `Communication Address of ${party}`
  }

  if (isPrimaryAnnuitantSection) {
    return "Communication Address of Primary Annuitant"
  }
}

const getSmartGuaranteedOccupationDetailsHeading = (
  section,
  isLaAndProposerSame
) => {
  const {
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (isSecondaryAnnuitantSection) {
    return "Occupation Details of Secondary Annuitant"
  }

  if (isProposerSection) {
    const party = getSmartGuaranteedProposerPartyLabel(isLaAndProposerSame)
    return `Occupation Details of ${party}`
  }

  if (isPrimaryAnnuitantSection) {
    return "Occupation Details of Primary Annuitant"
  }
}

const getSmartGuaranteedInsuranceDetailsHeading = (
  section,
  isLaAndProposerSame
) => {
  const {
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
    isInsuranceDetailsSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (!isInsuranceDetailsSection) {
    return
  }

  if (isSecondaryAnnuitantSection) {
    return "Insurance Details of Secondary Annuitant"
  }

  if (isProposerSection) {
    const party = getSmartGuaranteedProposerPartyLabel(isLaAndProposerSame)
    return `Insurance Details of ${party}`
  }

  if (isPrimaryAnnuitantSection) {
    return "Insurance Details of Primary Annuitant"
  }
}

const getSmartGuaranteedNriQuestionnairesHeading = (
  section,
  isLaAndProposerSame
) => {
  const {
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
    isNriQuestionnaireSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (!isNriQuestionnaireSection) {
    return
  }

  if (isSecondaryAnnuitantSection) {
    return "NRI Questionnaires of Secondary Annuitant"
  }

  if (isProposerSection) {
    const party = getSmartGuaranteedProposerPartyLabel(isLaAndProposerSame)
    return `NRI Questionnaires of ${party}`
  }

  if (isPrimaryAnnuitantSection) {
    return "NRI Questionnaires of Primary Annuitant"
  }
}

const getSmartGuaranteedOccupationalQuestionnaireHeading = (
  section,
  isLaAndProposerSame
) => {
  const {
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
    isOccupationalQuestionnaireSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (!isOccupationalQuestionnaireSection) {
    return
  }

  if (isSecondaryAnnuitantSection) {
    return "Occupational Questionaire of Secondary Annuitant"
  }

  if (isProposerSection) {
    const party = getSmartGuaranteedProposerPartyLabel(isLaAndProposerSame)
    return `Occupational Questionaire of ${party}`
  }

  if (isPrimaryAnnuitantSection) {
    return "Occupational Questionaire of Primary Annuitant"
  }
}

const getSmartGuaranteedBankDetailsHeading = (section, isLaAndProposerSame) => {
  const {
    isPrimaryAnnuitantSection,
    isSecondaryAnnuitantSection,
    isProposerSection,
    isBankDetailsSection,
  } = getSmartGuaranteedSectionMeta(section)

  if (!isBankDetailsSection) {
    return
  }

  if (isSecondaryAnnuitantSection) {
    return "Bank Details of Secondary Annuitant"
  }

  if (isProposerSection) {
    const party = getSmartGuaranteedProposerPartyLabel(isLaAndProposerSame)
    return `Bank Details of ${party}`
  }

  if (isPrimaryAnnuitantSection) {
    return "Bank Details of Primary Annuitant"
  }
}

const getSmartGuaranteedDirectHeadingOverride = (
  section,
  isLaAndProposerSame
) => {
  return (
    getSmartGuaranteedCommunicationAddressHeading(
      section,
      isLaAndProposerSame
    ) ||
    getSmartGuaranteedInsuranceDetailsHeading(section, isLaAndProposerSame) ||
    getSmartGuaranteedNriQuestionnairesHeading(section, isLaAndProposerSame) ||
    getSmartGuaranteedOccupationalQuestionnaireHeading(
      section,
      isLaAndProposerSame
    ) ||
    getSmartGuaranteedBankDetailsHeading(section, isLaAndProposerSame)
  )
}

const getSmartGuaranteedHeadingOverride = ({
  role,
  section,
  isLaAndProposerSame,
}) => {
  if (role === "personalDetails") {
    return getSmartGuaranteedPersonalDetailsHeading({
      section,
      isLaAndProposerSame,
    })
  }

  if (role === "residenceOccupation") {
    return getSmartGuaranteedResidenceOccupationHeading({
      section,
      isLaAndProposerSame,
    })
  }

  if (role === "communicationAddress") {
    return getSmartGuaranteedCommunicationAddressHeading(
      section,
      isLaAndProposerSame
    )
  }

  if (role === "occupationDetails") {
    return getSmartGuaranteedOccupationDetailsHeading(
      section,
      isLaAndProposerSame
    )
  }

  if (role === "nriQuestionnaires") {
    return getSmartGuaranteedNriQuestionnairesHeading(
      section,
      isLaAndProposerSame
    )
  }

  if (role === "occupationalQuestionnaire") {
    return getSmartGuaranteedOccupationalQuestionnaireHeading(
      section,
      isLaAndProposerSame
    )
  }
}

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const replaceHeadingContent = (content, valuesToReplace, nextHeading) => {
  if (!content || !nextHeading || !valuesToReplace.length) {
    return content || ""
  }

  const patterns = valuesToReplace.map(
    value => new RegExp(escapeRegExp(value), "gi")
  )

  return patterns.reduce(
    (updatedContent, pattern) => updatedContent.replace(pattern, nextHeading),
    content
  )
}

const getLaAndProposerSame = ({
  conditionState,
  sectionState,
  journeyState,
  sections,
  allowSectionInference = false,
}) => {
  const conditionValue = getConditionStateLaAndProposerSame(conditionState)

  if (conditionValue) {
    return conditionValue
  }

  const sectionValue = getSectionDataLaAndProposerSame(sectionState?.sectionData)

  if (sectionValue) {
    return sectionValue
  }

  const journeyValue = getJourneyDataLaAndProposerSame(journeyState)

  if (journeyValue) {
    return journeyValue
  }

  if (allowSectionInference) {
    return inferLaAndProposerSameFromSections(sections)
  }
}

export const getSectionDisplayName = ({
  section,
  conditionState,
  sectionState,
  journeyState,
  sections,
}) => {
  if (!section) {
    return ""
  }

  const isLaAndProposerSame = getLaAndProposerSame({
    conditionState,
    sectionState,
    journeyState,
    sections,
    allowSectionInference: true,
  })

  if (isSmartGuaranteedPensionJourney(journeyState)) {
    const directHeading = getSmartGuaranteedDirectHeadingOverride(
      section,
      isLaAndProposerSame
    )

    if (directHeading) {
      return appendSmartGuaranteedPartyToHeading(directHeading, section)
    }
  }

  const role = getHeadingRole(section)
  if (!role) {
    if (isSmartGuaranteedPensionJourney(journeyState)) {
      return appendSmartGuaranteedPartyToHeading(section.name || "", section)
    }

    return section.name || ""
  }

  if (isSmartGuaranteedPensionJourney(journeyState)) {
    const heading = getSmartGuaranteedHeadingOverride({
      role,
      section,
      isLaAndProposerSame,
    })

    if (heading) {
      return appendSmartGuaranteedPartyToHeading(heading, section)
    }
  }

  const defaultHeading =
    getHeadingTextForRole(role, isLaAndProposerSame, isProposerSection(section)) ||
    section.name ||
    ""

  if (isSmartGuaranteedPensionJourney(journeyState)) {
    return appendSmartGuaranteedPartyToHeading(defaultHeading, section)
  }

  return defaultHeading
}

export const getParentSectionDisplayName = ({
  section,
  journeyState,
  conditionState,
  sectionState,
  sections = [],
}) => {
  if (!section) {
    return ""
  }

  const isLaAndProposerSame = getLaAndProposerSame({
    conditionState,
    sectionState,
    journeyState,
    sections,
    allowSectionInference: true,
  })

  if (
    isSmartGuaranteedPensionJourney(journeyState) &&
    section.ParentSectionName === "Details of Proposer"
  ) {
    return isLaAndProposerSame === "N"
      ? "Details of Proposer"
      : "Details of Primary Annuitant"
  }

  if (
    isSmartGuaranteedPensionJourney(journeyState) &&
    section.ParentSectionName === "Bank Details of Proposer"
  ) {
    return isLaAndProposerSame === "N"
      ? "Bank Details of Proposer"
      : "Bank Details of Primary Annuitant"
  }

  return section.ParentSectionName || ""
}

export const getDynamicTextContent = ({
  sectionId,
  content,
  conditionState,
  sectionState,
  journeyState,
  sections,
}) => {
  const isLaAndProposerSame = getLaAndProposerSame({
    conditionState,
    sectionState,
    journeyState,
    sections,
    allowSectionInference: true,
  })

  if (isSmartGuaranteedPensionJourney(journeyState)) {
    const smartGuaranteedSection = { sectionId, name: content }
    const directHeading = getSmartGuaranteedDirectHeadingOverride(
      {
        sectionId,
        name: content,
      },
      isLaAndProposerSame
    )

    if (directHeading) {
      const nextHeading = appendSmartGuaranteedPartyToHeading(
        directHeading,
        smartGuaranteedSection
      )

      return replaceHeadingContent(
        content,
        [
          HEADING_TEMPLATES.communicationAddress.single,
          HEADING_TEMPLATES.communicationAddress.shared,
          HEADING_TEMPLATES.communicationAddress.proposer,
          HEADING_TEMPLATES.nriQuestionnaires.single,
          HEADING_TEMPLATES.nriQuestionnaires.shared,
          HEADING_TEMPLATES.nriQuestionnaires.proposer,
          HEADING_TEMPLATES.occupationalQuestionnaire.single,
          HEADING_TEMPLATES.occupationalQuestionnaire.shared,
          HEADING_TEMPLATES.occupationalQuestionnaire.proposer,
          "Insurance Details of Primary Annuitant",
          "Insurance Details of Secondary Annuitant",
          "Insurance Details of Proposer",
          "Communication Address of Primary Annuitant",
          "Communication Address of Secondary Annuitant",
          "Communication Address of Proposer",
          "NRI Questionnaires of Primary Annuitant",
          "NRI Questionnaires of Secondary Annuitant",
          "NRI Questionnaires of Proposer",
          "Occupational Questionaire of Primary Annuitant",
          "Occupational Questionaire of Secondary Annuitant",
          "Occupational Questionaire of Proposer",
          "Bank Details of Primary Annuitant",
          "Bank Details of Secondary Annuitant",
          "Bank Details of Proposer",
          "Insurance Details",
          "NRI Questionnaire",
          "NRI Questionnaires",
          "Occupational Questionnaire",
          "Occupational Questionaire",
        ],
        nextHeading
      )
    }
  }

  const role = getHeadingRole({ sectionId, name: content })
  if (!role) {
    return content || ""
  }

  if (isSmartGuaranteedPensionJourney(journeyState)) {
    const smartGuaranteedSection = { sectionId, name: content }
    const heading = getSmartGuaranteedHeadingOverride({
      role,
      section: smartGuaranteedSection,
      isLaAndProposerSame,
    })

    if (heading) {
      const nextHeading = appendSmartGuaranteedPartyToHeading(
        heading,
        smartGuaranteedSection
      )

      return replaceHeadingContent(
        content,
        [
          HEADING_TEMPLATES.personalDetails.single,
          HEADING_TEMPLATES.personalDetails.shared,
          HEADING_TEMPLATES.residenceOccupation.single,
          HEADING_TEMPLATES.residenceOccupation.shared,
          HEADING_TEMPLATES.communicationAddress.single,
          HEADING_TEMPLATES.communicationAddress.shared,
          HEADING_TEMPLATES.communicationAddress.proposer,
          HEADING_TEMPLATES.occupationDetails.single,
          HEADING_TEMPLATES.occupationDetails.shared,
          HEADING_TEMPLATES.occupationDetails.proposer,
          "Personal Details of Annuitant",
          "Personal Details of Primary Annuitant",
          "Personal Details of Secondary Annuitant",
          "Personal Details of Annuitant / Secondary Annuitant",
          "Personal Details of Proposer",
          "Primary Annuitant Residence and Occupation Details",
          "Secondary Annuitant Residence and Occupation Details",
          "Annuitant Residence and Occupation Details",
          "Annuitant / Secondary Annuitant Residence and Occupation Details",
          "Communication Address of Primary Annuitant",
          "Communication Address of Secondary Annuitant",
          "Communication Address of Proposer",
          "Occupation Details of Primary Annuitant",
          "Occupation Details of Secondary Annuitant",
          "Occupation Details of Proposer",
          "Insurance Details of Primary Annuitant",
          "Insurance Details of Secondary Annuitant",
          "Insurance Details of Proposer",
          "NRI Questionnaires of Primary Annuitant",
          "NRI Questionnaires of Secondary Annuitant",
          "NRI Questionnaires of Proposer",
          "Occupational Questionaire of Primary Annuitant",
          "Occupational Questionaire of Secondary Annuitant",
          "Occupational Questionaire of Proposer",
          "Bank Details of Primary Annuitant",
          "Bank Details of Secondary Annuitant",
          "Bank Details of Proposer",
        ],
        nextHeading
      )
    }
  }

  const isProposer = isProposerSection({ sectionId, name: content })
  const nextHeading = getHeadingTextForRole(role, isLaAndProposerSame, isProposer)
  const template = HEADING_TEMPLATES[role]

  if (!nextHeading || !template) {
    return content || ""
  }

  return replaceHeadingContent(content, [template.single, template.proposer, template.shared], nextHeading)
}
